import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { PlasticRoute } from '../../types';

/** 聚氯乙烯(PVC)乙烯法 P&ID 详细图：直氯化+氧氯化双线产 EDC → 混合裂解制 VCM（HCl 循环回氧氯化）→ 悬浮聚合 → 汽提干燥 */
const media = {
  ethylene: { label: '乙烯', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  chlorine: { label: '氯气', light: '#827717', dark: '#d4e157', width: 2 },
  oxygen: { label: '氧气', light: '#d32f2f', dark: '#e57373', width: 2 },
  edc: { label: '二氯乙烷(EDC)', light: '#00838f', dark: '#4dd0e1', width: 2.6 },
  vcm: { label: '氯乙烯单体(VCM)', light: '#5c6bc0', dark: '#9fa8da', width: 2.6 },
  hcl: { label: '氯化氢(HCl,循环)', light: '#c2185b', dark: '#f06292', width: 2, dash: '7 4' },
  pvc: { label: 'PVC浆料/粉料', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  waste: { label: '高沸残液(回收EDC)', light: '#616161', dark: '#b0bec5', width: 2 },
};

const devices = [
  { id: 'r101', kind: 'reactor' as const, x: 200, y: 128, tag: 'R-101', name: '直接氯化反应器', note: '乙烯+Cl₂→EDC' },
  { id: 'v101', kind: 'vDrum' as const, x: 330, y: 161, tag: 'V-101', name: 'EDC精制罐' },
  { id: 'j102', kind: 'mixer' as const, x: 344, y: 66, tag: '', name: '' },
  { id: 'r102', kind: 'reactor' as const, x: 430, y: -30, tag: 'R-102', name: '氧氯化反应器', note: '乙烯+2HCl+½O₂' },
  { id: 'j101', kind: 'mixer' as const, x: 448, y: 225, tag: '', name: '' },
  { id: 'f101', kind: 'furnace' as const, x: 500, y: 146, tag: 'F-101', name: 'EDC裂解炉', note: '500~550℃' },
  { id: 't101', kind: 'column' as const, x: 660, y: 101, tag: 'T-101', name: '急冷塔' },
  { id: 't102', kind: 'column' as const, x: 810, y: 94, tag: 'T-102', name: 'VCM精制塔', note: '脱轻重组分' },
  { id: 'r103', kind: 'reactor' as const, x: 960, y: 128, tag: 'R-103', name: '聚合釜', note: '50~70℃悬浮聚合' },
  { id: 't103', kind: 'column' as const, x: 1110, y: 101, tag: 'T-103', name: '汽提塔' },
  { id: 'v102', kind: 'vDrum' as const, x: 1250, y: 161, tag: 'V-102', name: '干燥器' },
];

const ports: PidPort[] = [
  { id: 'p-eth1', label: '乙烯', medium: 'ethylene', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-cl2', label: '氯气', medium: 'chlorine', x: 60, y: 40, direction: 'in' as const },
  { id: 'p-eth2', label: '乙烯', medium: 'ethylene', x: 240, y: 66, direction: 'in' as const },
  { id: 'p-o2', label: '氧气', medium: 'oxygen', x: 270, y: 130, direction: 'in' as const },
  { id: 'p-pvc', label: '聚氯乙烯(PVC)', medium: 'pvc', x: 1400, y: 224, direction: 'out' as const },
  { id: 'p-residue', label: '高沸残液(回收EDC)', medium: 'waste', x: 985, y: 620, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-eth1', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'ethylene' },
  { id: 's02', from: 'p-cl2', fromSide: 'right', to: 'r101', toSide: 'top', medium: 'chlorine' },
  { id: 's03', from: 'r101', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'edc' },
  { id: 's04', from: 'v101', fromSide: 'right', to: 'j101', toSide: 'left', medium: 'edc' },
  { id: 's05', from: 'r102', fromSide: 'bottom', to: 'j101', toSide: 'top', medium: 'edc' },
  { id: 's06', from: 'j101', fromSide: 'right', to: 'f101', toSide: 'left', medium: 'edc' },
  { id: 's07', from: 'f101', fromSide: 'right', to: 't101', toSide: 'left', medium: 'vcm', label: '裂解气(VCM+HCl)' },
  { id: 's08', from: 't101', fromSide: 'top', to: 'r102', toSide: 'right', medium: 'hcl' },
  { id: 's09', from: 't101', fromSide: 'right', to: 't102', toSide: 'left', medium: 'vcm' },
  { id: 's10', from: 'p-eth2', fromSide: 'right', to: 'j102', toSide: 'left', medium: 'ethylene' },
  { id: 's11', from: 'p-o2', fromSide: 'right', to: 'j102', toSide: 'bottom', medium: 'oxygen' },
  { id: 's12', from: 'j102', fromSide: 'right', to: 'r102', toSide: 'left', medium: 'oxygen' },
  { id: 's13', from: 't102', fromSide: 'right', to: 'r103', toSide: 'left', medium: 'vcm' },
  { id: 's14', from: 'r103', fromSide: 'right', to: 't103', toSide: 'left', medium: 'pvc' },
  { id: 's15', from: 't103', fromSide: 'right', to: 'v102', toSide: 'left', medium: 'pvc' },
  { id: 's16', from: 'v102', fromSide: 'right', to: 'p-pvc', toSide: 'left', medium: 'pvc' },
  { id: 's17', from: 't102', fromSide: 'bottom', to: 'p-residue', toSide: 'left', medium: 'waste' },
];

const PvcEthyleneDiagram: React.FC<{ route: PlasticRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="乙烯法PVC装置详细流程"
    subtitle="直接氯化与氧氯化双线产二氯乙烷(EDC) → 混合裂解(500~550℃)制氯乙烯单体(VCM) → 裂解副产HCl循环回氧氯化实现氯平衡 → VCM悬浮聚合 → 汽提 → 干燥（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default PvcEthyleneDiagram;
